import React, { useEffect, useRef, useState } from "react";
import css from "@emotion/css";
import styled from "@emotion/styled";
import Hammer from "hammerjs";

import ValueAdjuster from "./ValueAdjuster";
import PlayButton from "./PlayButton";
import useWindowSize from "../../../utils/useWindowSize";
import { useRecoilState } from "recoil";
import keys, { Flat, Minor } from "../keys";

// TODO: move the recoil objects under NotePlayer folder after refactoring
import baseKeyState from "../../../recoil/baseKeyState";
import speedState from "../../../recoil/speedState";
import chordSizeState from "../../../recoil/chordSizeState";
import isNotePlayerLoadingState from "../../../recoil/isNotePlayerLoadingState";
import capoState from "../../../recoil/capoState";

import SpeedIcon from "../../../assets/icons/speed.svg";
import KeyIcon from "../../../assets/icons/key.svg";
import ChordLyricsIcon from "../../../assets/icons/chord_lyrics.svg";
import CloseIcon from "../../../assets/icons/close.svg";
import ResetIcon from "../../../assets/icons/reset.png";
import MetronomeButton from "./MetronomeButton";
import { PlaylistRow } from "../../PlaylistModal";
import { getPlaylist, savePlaylist, makeNewPlaylist } from "../../../utils/playlist";
import { IPlaylist } from "../../../types";
import auth from "../../../utils/auth";
import { useHistory } from "react-router";
import Modal from "../../Modal";
import Input from "../../Input";
import { track } from "../../../utils/track";
import { click as metroClick, prepareMetronome, tapTempo } from "../metronome";
interface IControllerWrapper {
  isControllerHidden?: boolean;
}

interface IController extends IControllerWrapper {
  info: any;
  personal?: boolean; // 내 악보: 연주목록 담기 숨김
  isPlaying?: boolean;
  onClickToggleController?: () => void;
  onClickPlayButton: () => void;
  hideProgress: boolean;
  enableMetronome?: boolean;
  onClickMetronome: () => void;
  onClickReset: () => void;
  /** 0~1 진행 비율과 곡 길이(초): 조절판의 진행 막대·시간 표시용 */
  progressRatio?: number;
  durationSec?: number;
  /** 반주(기타 소리) 상태와 바꾸기 */
  accStyle?: "off" | "strum" | "arpeggio";
  onClickAccompany?: () => void;
  /** 원곡 빠르기(BPM). 속도 옆 표시와 탭 템포 계산용 */
  songBpm?: number;
  /** 연주 체크(마이크) */
  practiceOn?: boolean;
  onClickPractice?: () => void;
}

const Controller: React.FC<IController> = ({
  personal = false,
  isPlaying = false,
  isControllerHidden = false,
  onClickToggleController,
  onClickPlayButton,
  hideProgress,
  info,
  enableMetronome,
  onClickMetronome,
  onClickReset,
  progressRatio = 0,
  durationSec = 0,
  accStyle = "off",
  onClickAccompany,
  songBpm = 0,
  practiceOn = false,
  onClickPractice,
}) => {
  const [capo, setCapo] = useRecoilState(capoState);
  const [speed, setSpeed] = useRecoilState(speedState);
  const [baseKey, setBaseKey] = useRecoilState(baseKeyState);
  const [chordSize, setChordSize] = useRecoilState(chordSizeState);
  const [playlists, setPlaylists] = useState<Array<IPlaylist>>([]);
  const [isOpenPlaylistModal, setIsOpenPlaylistModal] = useState(false);
  const PlayListIcon = require(`../../../assets/songrow-icon-1.svg`).default;
  const history = useHistory();
  const [isNewPlaylist, setIsNewPlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState<string>('');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isNotePlayerLoading, setIsNotePlayerLoading] = useRecoilState(
    isNotePlayerLoadingState
  );

  const { width: screenWidth = 1980 } = useWindowSize();
  const isSmallScreen = screenWidth <= 840;

  // 컨트롤러 상단 흰 막대: 누르거나 아래로 끌어내리면 화면 밖으로 완전히 내려가고,
  // 내려간 뒤에는 아래 가운데 작은 손잡이를 누르거나 위로 밀면 다시 올라온다.
  const [dragY, setDragY] = useState<number | null>(null);
  const dragStart = useRef<{ y: number; moved: boolean } | null>(null);
  const DRAG_HIDE_PX = 40;

  const onHandleTouchStart = (e: React.TouchEvent) => {
    dragStart.current = { y: e.touches[0].clientY, moved: false };
  };
  const onHandleTouchMove = (e: React.TouchEvent) => {
    if (!dragStart.current) return;
    const dy = e.touches[0].clientY - dragStart.current.y;
    if (Math.abs(dy) > 6) dragStart.current.moved = true;
    setDragY(Math.max(0, dy));
  };
  const onHandleTouchEnd = (e: React.TouchEvent) => {
    const start = dragStart.current;
    dragStart.current = null;
    const dy = dragY || 0;
    setDragY(null);
    if (!start) return;
    if (start.moved) {
      e.preventDefault(); // 끌기였으면 뒤따르는 click(토글)을 막는다
      if (dy > DRAG_HIDE_PX && onClickToggleController) onClickToggleController();
    }
  };

  const tabStart = useRef<number | null>(null);
  const onTabTouchStart = (e: React.TouchEvent) => {
    tabStart.current = e.touches[0].clientY;
  };
  const onTabTouchEnd = (e: React.TouchEvent) => {
    const start = tabStart.current;
    tabStart.current = null;
    if (start === null) return;
    const dy = e.changedTouches[0].clientY - start;
    if (dy < -20) {
      e.preventDefault();
      if (onClickToggleController) onClickToggleController();
    }
  };

  const setSpeedPlus = () => {
    track("score:speed");
    setSpeed(Number(parseFloat((speed + 0.1).toString()).toPrecision(12)));
  };

  // 탭 템포: 원하는 빠르기로 '탭'을 3번 이상 두드리면 속도가 맞춰진다
  const lastTapTrack = useRef(0);
  const onTapTempo = () => {
    prepareMetronome();
    metroClick(false);
    const bpm = tapTempo();
    if (!bpm || !songBpm) return;
    setSpeed(Math.min(2, Math.max(0.3, Math.round((bpm / songBpm) * 20) / 20)));
    if (Date.now() - lastTapTrack.current > 5000) track("score:tap");
    lastTapTrack.current = Date.now();
  };
  const speedText = Math.abs(speed * 10 - Math.round(speed * 10)) < 0.001 ? speed.toFixed(1) : speed.toFixed(2);

  const setSpeedMinus = () => {
    if (speed - 0.1 > 0) {
      setSpeed(Number(parseFloat((speed - 0.1).toString()).toPrecision(12)));
    }
  };

  // 2026-10-10 사장님 승인(a-qa-barfont): PC 기본 1 → 1.5 (한 줄 4마디 → 3마디, 글씨 1.5배). 휴대폰은 2 그대로 두고 NoteBar 에서 글씨만 키움
  // 글자 크기: 휴대폰(2)·PC(1.5)가 기본. 고른 크기는 기기 종류별로 기억해서 다음에도·화면을 돌려도 유지 (10/10 품질 점검)
  const sizeKey = () => (window.innerWidth <= 480 ? 'cs_chord_size_m' : 'cs_chord_size_pc');
  const baseChordSize = () => (window.innerWidth <= 480 ? 2 : 1.5);
  const saveChordSize = (v: number) => {
    try { localStorage.setItem(sizeKey(), String(v)); } catch (e) {}
  };
  const updateWindowDimensions = () => {
    let saved = 0;
    try { saved = Number(localStorage.getItem(sizeKey())) || 0; } catch (e) {}
    setChordSize(saved >= 1 && saved <= 4 ? saved : baseChordSize());
  };

  const refChordsize = useRef<number>(chordSize);

  useEffect(() => {
    refChordsize.current = chordSize;
  }, [chordSize]);

  const handlePinAndPan = () => {
    const el = document.getElementById('note-wrap') as HTMLElement;
    var hammer = new Hammer(el, {
      domEvents: true,
      enable: true,
    });

    hammer.get('pinch').set({
      enable: true,
    });
    el.style.touchAction = 'unset';
    let countPinchin = 0;
    let countPinchout = 0;

    hammer.on('pinchin', (e) => {
      countPinchin += 1;
    })

    hammer.on('pinchout', (e) => {
      countPinchout += 1;
    })

    hammer.on('pinchcancel', (e) => {
      console.log(countPinchin - countPinchout);
      if (countPinchin > countPinchout) {
        if (refChordsize.current - 0.5 >= 1) {
          setChordSize((prevSize) => prevSize - 0.5);
          saveChordSize(refChordsize.current - 0.5);
        }
      }
      if (countPinchin < countPinchout) {
        if (refChordsize.current + 0.5 <= 4) {
          setChordSize((prevSize) => prevSize + 0.5);
          saveChordSize(refChordsize.current + 0.5);
        }
      }
      countPinchin = 0;
      countPinchout = 0;
    })
  }


  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener("resize", updateWindowDimensions);
    handlePinAndPan();

    return () => window.removeEventListener("resize", updateWindowDimensions)
  }, []);

  useEffect(() => {
    if (isOpenPlaylistModal) {
      getPlaylist().then((playlists) => setPlaylists(playlists));
    }
  }, [isOpenPlaylistModal]);

  const onClickNextBaseKey = () => {
    track("score:key");
    if (capo) setCapo(null);
    const nextIndex = baseKey + 1;
    setBaseKey(nextIndex);
  };

  const onClickPrevBaseKey = () => {
    track("score:key");
    if (capo) setCapo(null);
    const prevIndex = baseKey - 1;

    if (info.original_key.replace(/♭/g, "ᵇ").indexOf("ᵇ") >= 0) {
      setBaseKey(
        prevIndex < 0
          ? (Flat.length - Math.abs(prevIndex)) % Flat.length
          : prevIndex
      );
    } else if (info.original_key.indexOf("m") >= 0) {
      setBaseKey(
        prevIndex < 0
          ? (Flat.length - Math.abs(prevIndex)) % Flat.length
          : prevIndex
      );
    } else {
      setBaseKey(
        prevIndex < 0
          ? (keys.length - Math.abs(prevIndex)) % keys.length
          : prevIndex
      );
    }
  };

  // 원래 키 이름 (2026-10-17 신고 15205): 'E♭m'·'Amᵇ'·'Bm7' 처럼 적힌 키는 목록에서 못 찾아 빈칸으로 나왔다 → 근음·단조만 떼어 계산
  const originalKey: string = (info.original_key || "").trim().replace(/♭/g, "ᵇ").replace(/^([A-G])b/, "$1ᵇ").replace(/^([A-G])mᵇ/, "$1ᵇm");
  const keyParts = originalKey.match(/^([A-G][ᵇ#]?)(m?)/);
  const keyLabel: string = (() => {
    if (!keyParts) return originalKey;
    const [, root, minor] = keyParts;
    if (root.indexOf("ᵇ") >= 0) {
      const i = Flat.indexOf(root);
      return i < 0 ? originalKey : Flat[(baseKey + i) % Flat.length] + minor;
    }
    const sharp = root.replace("#", "<sup>#</sup>");
    if (minor) {
      const i = Minor.indexOf(sharp + "m");
      return i < 0 ? originalKey : Minor[(baseKey + i) % Minor.length];
    }
    const i = keys.indexOf(sharp);
    return i < 0 ? originalKey : keys[(baseKey + i) % keys.length];
  })();

  const onClickPrevChordSize = () => {
    setIsNotePlayerLoading(true);
    if (chordSize + 0.5 <= 4) {
      setChordSize((prevSize) => prevSize + 0.5);
      saveChordSize(chordSize + 0.5);
    }
    setTimeout(() => {
      setIsNotePlayerLoading(false);
    }, 500);
  };

  const onClickNextChordSize = () => {
    setIsNotePlayerLoading(true);
    if (chordSize - 0.5 >= 1) {
      setChordSize((prevSize) => prevSize - 0.5);
      saveChordSize(chordSize - 0.5);
    }
    setTimeout(() => {
      setIsNotePlayerLoading(false);
    }, 500);
  };

  return (
    <>
      {isControllerHidden && (
        <ShowControllerTab
          aria-label="컨트롤러 열기"
          onClick={onClickToggleController}
          onTouchStart={onTabTouchStart}
          onTouchEnd={onTabTouchEnd}
        >
          <span />
        </ShowControllerTab>
      )}
      <ControllerWrapper
        isControllerHidden={isControllerHidden}
        style={dragY !== null ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
      >
        <ControllerContainer isControllerHidden={isControllerHidden}>
          {/* {isControllerHidden ? (
            <OpenIconWrapper>
              <CloseIcon />
            </OpenIconWrapper>
          ) : ( */}
            <ControllerContent>
              <ShowHideControllerPanel
                aria-label="조절판 내리기"
                onClick={onClickToggleController}
                onTouchStart={onHandleTouchStart}
                onTouchMove={onHandleTouchMove}
                onTouchEnd={onHandleTouchEnd}
              >
                <span />
              </ShowHideControllerPanel>
              <TopRow>
                <PlayCircle
                  type="button"
                  aria-label={isPlaying ? "자동 스크롤 멈춤" : "자동 스크롤 시작"}
                  onClick={onClickPlayButton}
                >
                  {isPlaying ? (
                    <svg viewBox="0 0 24 24"><path d="M6 4h4v16H6zM14 4h4v16h-4z" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24"><path d="M7 4.5v15l13-7.5z" /></svg>
                  )}
                </PlayCircle>
                <IconButton type="button" aria-label="처음부터" onClick={onClickReset}>
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" />
                  </svg>
                </IconButton>
                <ProgressBox />
                {onClickAccompany && (
                  <AccButton type="button" active={accStyle !== "off"} onClick={onClickAccompany}
                    aria-label={accStyle === "off" ? "기타 반주 켜기" : accStyle === "strum" ? "기타 반주: 스트로크 (누르면 아르페지오)" : "기타 반주: 아르페지오 (누르면 끄기)"}>
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 3l2 2-6.5 6.5M14.5 11.5a4 4 0 0 0-5.6-.4l-.6.6a3 3 0 0 1-2 .8 3 3 0 0 0-2.1 5.1l.6.6a3 3 0 0 0 5.1-2.1 3 3 0 0 1 .8-2l.6-.6a4 4 0 0 0-.4-5.6zM9 15l-1 1" />
                    </svg>
                    <span>{accStyle === "off" ? "반주" : accStyle === "strum" ? "스트럼" : "핑거"}</span>
                  </AccButton>
                )}
                <IconButton type="button" aria-label="메트로놈" active={!!enableMetronome} onClick={onClickMetronome}>
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 3h6l4 18H5zM12 15l5-8M7.5 15h9" />
                  </svg>
                </IconButton>
                {onClickPractice && (
                  <IconButton type="button" aria-label={practiceOn ? "연주 체크 끄기" : "연주 체크: 마이크로 듣고 맞게 치는지 알려 줘요"} active={practiceOn} onClick={onClickPractice}>
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM5 11a7 7 0 0 0 14 0M12 18v3" />
                    </svg>
                  </IconButton>
                )}
                {!personal && <IconButton
                  type="button"
                  aria-label="연주목록에 담기"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    auth.getToken()
                      ? setIsOpenPlaylistModal(true)
                      : history.push('/playlist');
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                  </svg>
                </IconButton>}
              </TopRow>
              {/* 진행 막대는 버튼 줄 아래 한 줄 전체 (버튼이 많아져도 시간이 겹치지 않게) */}
              <ProgressRow>
                {!hideProgress && durationSec > 0 && <TimeText>{fmtTime(durationSec * Math.min(1, progressRatio))}</TimeText>}
                <ProgressTrack>
                  <ProgressFill style={{ width: `${Math.min(100, Math.max(0, progressRatio * 100))}%` }} />
                </ProgressTrack>
                {!hideProgress && durationSec > 0 && <TimeText>{fmtTime(durationSec)}</TimeText>}
              </ProgressRow>
              <StepperRow>
                {!hideProgress && (
                  <Stepper
                    label="속도"
                    labelExtra={
                      songBpm > 0 ? (
                        <>
                          <BpmText>♩{Math.round(songBpm * speed)}</BpmText>
                          <TapChip type="button" aria-label="탭 템포: 원하는 빠르기로 여러 번 누르세요" onClick={onTapTempo}>탭</TapChip>
                        </>
                      ) : null
                    }
                    value={`${speedText}x`}
                    onMinus={setSpeedMinus}
                    onPlus={setSpeedPlus}
                  />
                )}
                <Stepper
                  label={capo ? `키 · 카포 ${capo.capo}` : "키"}
                  value={keyLabel}
                  accent
                  onMinus={onClickPrevBaseKey}
                  onPlus={onClickNextBaseKey}
                />
                <Stepper
                  label="글자 크기"
                  value={`${Math.round((chordSize / baseChordSize()) * 100)}%`}
                  onMinus={onClickNextChordSize}
                  onPlus={onClickPrevChordSize}
                />
              </StepperRow>
            </ControllerContent>
          {/* )} */}
        </ControllerContainer>
      </ControllerWrapper>
      {isOpenPlaylistModal && (
        <Modal
          open={isOpenPlaylistModal}
          onClose={() => setIsOpenPlaylistModal(false)}
          title={!isNewPlaylist ? '연주목록에 담기' : '새 연주목록'}
          firstButtonLabel={
            !isNewPlaylist ? '새 연주목록 만들기' : '생성하기'
          }
          onClickFirstButton={
            isNewPlaylist
              ? async () => {
                await makeNewPlaylist(newPlaylistName);
                const playlists = await getPlaylist();
                setPlaylists(playlists);
                setIsNewPlaylist(false);
              }
              : () => setIsNewPlaylist(true)
          }
          secondButtonLabel="취소"
          onClickSecondButton={() =>
            isNewPlaylist
              ? setIsNewPlaylist(false)
              : setIsOpenPlaylistModal(false)
          }
          content={
            <>
              {!isNewPlaylist ? (
                <PlaylistWrap>
                  {playlists.length === 0 && (<div style={{ padding: '16px 0', textAlign: 'center', color: '#9aa0a6', fontSize: 14 }}>아직 연주목록이 없어요.<br />아래 '새 연주목록 만들기'를 눌러 주세요.</div>)}
                  {playlists.map((playlist) => (
                    <PlaylistRow
                      key={`playlist-${playlist.id}`}
                      id={playlist.id}
                      title={playlist.name}
                      count={playlist.total_count}
                      onClick={async (playlistId: number) => {
                        await savePlaylist(playlistId, info.id.toString());
                        const playlists = await getPlaylist();
                        setPlaylists(playlists);
                        setIsOpenPlaylistModal(false);
                      }}
                    />
                  ))}
                </PlaylistWrap>
              ) : (
                <StyledInput
                  icon="edit"
                  placeholder="연주목록 이름을 입력해주세요"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                />
              )}
            </>
          }
        />
      )}
    </>
  );
};

export default Controller;

const ControllerWrapper = styled.div<IControllerWrapper>`
  width: 100%;
  position: fixed;
  z-index: 20; /* 악보 위 '2/4' 같은 마디 표시(z-index 1)가 조절판 위로 올라오지 않게 (2026-10-17) */
  left: 0;
  right: 0;
  bottom: 0;
  bottom: constant(safe-area-inset-bottom);
  bottom: env(safe-area-inset-bottom);
  display: flex;
  transition: transform 0.25s ease;
  cursor: pointer;
  /* 화면 가로 전체를 덮는 투명한 틀이 손가락 스크롤을 막지 않게 (가로 모드에서 컨트롤러 양옆) */
  pointer-events: none;

  @media screen and (max-width: 360px) {
    position: fixed;
    z-index: 999;
  }

  ${({ isControllerHidden = false }) =>
    isControllerHidden &&
    css`
      transform: translateY(calc(100% + 40px));
      pointer-events: none;
    `};
`;

// 컨트롤러를 내린 뒤 다시 여는 작은 손잡이 (악보를 가리지 않게 작고 반투명하게)
const ShowControllerTab = styled.button`
  position: fixed;
  left: 50%;
  bottom: 0;
  bottom: env(safe-area-inset-bottom);
  transform: translateX(-50%);
  z-index: 999;
  width: 96px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 14px 14px 0 0;
  background: rgba(27, 30, 38, 0.92);
  border: 1px solid #2a2e38;
  border-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: 0;

  & > span {
    display: block;
    width: 40px;
    height: 5px;
    border-radius: 3px;
    background: #7c8392;
  }
`;

interface IButton {
  checked?: boolean;
  isOpacityDisabled?: boolean;
}
const Button = styled.button<IButton>`
  border: solid 1px rgba(255,255,255,0.41);
  padding: 0;
  outline: 0;
  background: none;

  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  min-width: 37px;
  min-height: 37px;
  background-color: rgba(74, 74, 75, 0.05);
  border-radius: 6px;
  margin-left: auto;
  &:last-of-type {
    margin-right: 0;
  }

  @media screen and (max-width: 840px) {
    width: 37px;
    height: 37px;
  }

  html[color-mode='dark'] & {
    background-color: rgba(216, 216, 216, 0.09);

    & path {
      /* opacity: ${({ isOpacityDisabled }) =>
    isOpacityDisabled ? 1 : 0.4} !important; */
      fill: #ffffff !important;
    }
  }

  ${({ checked }) =>
    checked &&
    css`
      background-color: rgba(10, 98, 186, 0.69);
      html[color-mode='dark'] & {
        background-color: rgba(10, 10, 11, 0.35);
        & path {
          fill: white !important;
          opacity: 0.53;
        }
      }
    `}
`;

const StyledInput = styled(Input)`
  margin-top: 36px;

  html[color-mode='dark'] & {
    background-color: #2e3137;
  }
`;

const ControllerContainer = styled.div<IControllerWrapper>`
  pointer-events: auto;
  width: calc(100% - 24px);
  max-width: 560px;
  margin: 0 auto 12px;
  box-sizing: border-box;
  background-color: #1b1e26;
  border: 1px solid #2a2e38;
  border-radius: 22px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
`;

const ControllerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 2px 12px 12px;
  position: relative;
`;

const ShowHideControllerPanel = styled.div`
  align-self: center;
  width: 120px;
  /* 누르기 쉽게 위아래 터치 영역을 넓힌 작은 손잡이 */
  padding: 8px 0 4px;
  touch-action: none;
  cursor: pointer;
  display: flex;
  justify-content: center;

  & > span {
    display: block;
    width: 36px;
    height: 4px;
    background-color: #3a3f4b;
    border-radius: 2px;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PlayCircle = styled.button`
  flex: none;
  width: 50px;
  height: 50px;
  border-radius: 25px;
  border: 0;
  padding: 0;
  background: #5b8cff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: 0;

  & > svg {
    width: 18px;
    height: 18px;
    fill: #0d0e12;
  }
`;

const IconButton = styled.button<{ active?: boolean }>`
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid ${({ active }) => (active ? "#5b8cff" : "#2a2e38")};
  background: ${({ active }) => (active ? "#2b3550" : "transparent")};
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: 0;

  & > svg {
    width: 20px;
    height: 20px;
    stroke: #f3f4f6;
  }
`;

const AccButton = styled.button<{ active: boolean }>`
  flex: none;
  height: 40px;
  padding: 0 8px;
  border-radius: 12px;
  border: 1px solid ${({ active }) => (active ? "#ffb547" : "#2a2e38")};
  background: ${({ active }) => (active ? "#2a2416" : "transparent")};
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  outline: 0;
  & > svg {
    width: 18px;
    height: 18px;
    stroke: ${({ active }) => (active ? "#ffb547" : "#f3f4f6")};
  }
  & > span {
    font-size: 11px;
    color: ${({ active }) => (active ? "#ffb547" : "#a3a9b5")};
    white-space: nowrap;
  }
`;

const ProgressBox = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  & > div {
    flex: 1;
  }
`;

const TimeText = styled.span`
  font-family: "IBM Plex Mono", ui-monospace, Menlo, monospace;
  font-size: 12px;
  color: #a3a9b5;
  white-space: nowrap;
`;

const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 2px;
  background: #2a2e38;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 4px;
  border-radius: 2px;
  background: #5b8cff;
  transition: width 0.3s linear;
`;

const StepperRow = styled.div`
  display: flex;
  gap: 8px;

  & > * {
    flex: 1;
    min-width: 0;
  }
`;

const StepperWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepperLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  font-size: 11px;
  color: #7c8392;
  text-align: center;
  white-space: nowrap;
`;

const StepperBox = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  border-radius: 12px;
  background: #0d0e12;
  border: 1px solid #2a2e38;
`;

const StepperBtn = styled.button`
  flex: none;
  width: 36px;
  height: 40px;
  border: 0;
  padding: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: 0;

  & > svg {
    width: 14px;
    height: 14px;
    stroke: #f3f4f6;
    stroke-width: 2.5;
    stroke-linecap: round;
    fill: none;
  }
`;

const StepperValue = styled.span<{ accent?: boolean }>`
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: ${({ accent }) => (accent ? "#ffb547" : "#f3f4f6")};
  white-space: nowrap;
  overflow: hidden;

  & sup {
    font-size: 0.7em;
  }
`;

const fmtTime = (sec: number) => {
  const t = Math.max(0, Math.round(sec));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

const BpmText = styled.span`
  color: #a3a9b5;
  margin-left: 4px;
  font-variant-numeric: tabular-nums;
`;

const TapChip = styled.button`
  margin-left: 6px;
  height: 22px;
  padding: 0 10px;
  border-radius: 11px;
  border: 1px solid #ffb547;
  background: transparent;
  color: #ffb547;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
  outline: 0;
  &:active {
    background: #2a2416;
  }
`;

const Stepper: React.FC<{ label: string; labelExtra?: React.ReactNode; value: string; accent?: boolean; onMinus: () => void; onPlus: () => void }> = ({
  label,
  labelExtra,
  value,
  accent,
  onMinus,
  onPlus,
}) => (
  <StepperWrap>
    <StepperLabel>
      {label}
      {labelExtra}
    </StepperLabel>
    <StepperBox>
      <StepperBtn type="button" aria-label={`${label} 낮추기`} onClick={onMinus}>
        <svg viewBox="0 0 24 24"><path d="M5 12h14" /></svg>
      </StepperBtn>
      <StepperValue accent={accent} dangerouslySetInnerHTML={{ __html: value }} />
      <StepperBtn type="button" aria-label={`${label} 올리기`} onClick={onPlus}>
        <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
      </StepperBtn>
    </StepperBox>
  </StepperWrap>
);

const ResetIconButton = styled.div`
  background-image: url("${ResetIcon}");
  background-size: cover;
  height: 50px;
  width: 50px;
  cursor: pointer;

  @media screen and (max-width: 840px) {
    height: 43px;
    width: 43px;
  }
`
const SmallScreenContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const AdjusterList = styled.div`
  display: flex;
  align-items: flex-end;
  margin-left: 50px;

  @media screen and (max-width: 840px) {
    // margin-left: 16px;
    margin-left: 0px;
  }
`;

const PlayButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

const MetronomeContainer = styled.div`
margin-left: 50px;

@media screen and (max-width: 840px) {
  margin-left: 0px;
}
`;

const CloseControllerButton = styled.div`
  margin-top: 12px;
  cursor: pointer;

  & > svg {
    width: 23px;
    height: 11px;
  }

  @media screen and (max-width: 840px) {
    margin-top: 10px;
  }
`;

const OpenIconWrapper = styled.div`
  & > svg {
    transform: rotate(180deg);
    height: 11px;
    width: 23px;
  }
`;

const FieldText = styled.span`
  margin-bottom: 11px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.23;
  color: #ffffff;
  height: ${13 * 1.23}px;
  text-align: center;

  @media screen and (max-width: 840px) {
    height: unset;
  }
`;

const PlaylistContainerWrap = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-left: 50px;
  @media screen and (max-width: 840px) {
    margin-left: 0px;
  }
`;

export const PlaylistWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 224px;
  border-radius: 5px;
  background-color: #f1f1f1;
  padding: 15px;
  overflow-x: hidden;
  overflow-y: scroll;

  html[color-mode='dark'] & {
    background-color: rgba(216, 216, 216, 0.05);
  }
`;

const PlaylistButton = styled(Button)`
  @media screen and (max-width: 840px) {
    margin: 14px 0px;
  }
`